import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { waHref } from "@/lib/wa";

const MENU = [
  { label: "Beranda", href: "#beranda" },
  { label: "Tentang Kami", href: "#tentang" },
  { label: "Perumahan", href: "#perumahan" },
  { label: "Tipe Rumah", href: "#tipe-rumah" },
  { label: "Fasilitas", href: "#fasilitas" },
  { label: "Lokasi", href: "#lokasi" },
  { label: "Galeri", href: "#galeri" },
  { label: "FAQ", href: "#faq" },
  { label: "Kontak", href: "#kontak" },
];

export function scrollToSection(href) {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function Navbar({ content }) {
  const [open, setOpen] = useState(false);
  const cta = waHref(content);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (href.startsWith("http")) window.open(href, "_blank", "noopener");
    else scrollToSection(href);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-100 bg-white/80 backdrop-blur-xl" data-testid="navbar">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <a href="#beranda" onClick={(e) => go(e, "#beranda")} className="flex items-center gap-3" data-testid="navbar-logo">
          <img src="/assets/logo.jpeg" alt="Logo PT. Cahaya Sakofa Mulia" className="h-10 w-10 rounded-lg object-cover" />
          <div className="leading-tight">
            <span className="block font-display text-sm font-bold tracking-tight text-brand-dark sm:text-base">
              PT. CAHAYA SAKOFA MULIA
            </span>
            <span className="hidden text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500 sm:block">
              Developer Perumahan Type 36
            </span>
          </div>
        </a>

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Navigasi utama">
          {MENU.map((m) => (
            <a
              key={m.href}
              href={m.href}
              onClick={(e) => go(e, m.href)}
              data-testid={`nav-link-${m.href.slice(1)}`}
              className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-brand"
            >
              {m.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={cta}
            onClick={(e) => go(e, cta)}
            data-testid="nav-cta-marketing"
            className="hidden items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-deep active:scale-95 sm:inline-flex"
          >
            <MessageCircle className="h-4 w-4" />
            Tanya Marketing
          </a>
          <button
            className="rounded-md p-2 text-brand-dark lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Buka menu"
            data-testid="nav-hamburger"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-slate-100 bg-white lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              {MENU.map((m) => (
                <a
                  key={m.href}
                  href={m.href}
                  onClick={(e) => go(e, m.href)}
                  data-testid={`nav-mobile-${m.href.slice(1)}`}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 transition-colors duration-200 hover:bg-brand-mist hover:text-brand"
                >
                  {m.label}
                </a>
              ))}
              <a
                href={cta}
                onClick={(e) => go(e, cta)}
                data-testid="nav-mobile-cta-marketing"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white active:scale-95"
              >
                <MessageCircle className="h-4 w-4" />
                Tanya Marketing
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
